import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Search, X } from "lucide-react";
import ShopImage from "../ShopImage";
import { safeImage, safeName, safePrice } from "../../utils/productHelpers";
import { ModalProductGridSkeleton } from "../skeletons/ShopperSkeletons";

function formatPrice(product) {
  return safePrice(product).toLocaleString("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
  });
}

export default function ProductSearchModal({
  open,
  inputRef,
  query,
  onQueryChange,
  onKeyDown,
  onSubmit,
  onViewAll,
  onClose,
  products = [],
  loading = false,
  pending = false,
  highlightedIndex = -1,
  onHighlight,
  onSelect,
  inputAriaLabel = "Search products",
  inputPlaceholder = "Search products...",
  inputA11yProps = {},
}) {
  const backdropRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    const previousActiveElement = document.activeElement;
    document.body.style.overflow = "hidden";

    const handleDocumentKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== "Tab") return;

      const focusable = panelRef.current?.querySelectorAll(
        'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable?.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleDocumentKeyDown);
    inputRef?.current?.focus();

    return () => {
      document.removeEventListener("keydown", handleDocumentKeyDown);
      document.body.style.overflow = previousOverflow;
      previousActiveElement?.focus?.();
    };
  }, [inputRef, onClose, open]);

  if (!open) return null;

  const normalizedQuery = String(query || "").trim();
  const isSearchQuery = normalizedQuery.length >= 1;
  const items = Array.isArray(products) ? products : [];

  return createPortal(
    <div
      ref={backdropRef}
      className="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto bg-black/55 px-3 py-[12vh] backdrop-blur-sm sm:px-6"
      onMouseDown={(event) => {
        if (event.target === backdropRef.current) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="product-search-title"
    >
      <section
        ref={panelRef}
        className="w-full max-w-4xl overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-2xl dark:border-neutral-700 dark:bg-neutral-900"
      >
        <h2 id="product-search-title" className="sr-only">Search Apparify products</h2>

        <div className="flex items-center border-b border-neutral-200 px-3 dark:border-neutral-800 sm:px-5">
          <button
            type="button"
            onClick={onSubmit}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-950 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-white"
            aria-label="Submit product search"
          >
            {pending ? (
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-transparent border-r-neutral-500 border-t-neutral-500" aria-hidden="true" />
            ) : (
              <Search size={20} />
            )}
          </button>

          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder={inputPlaceholder}
            className="min-h-16 min-w-0 flex-1 bg-transparent px-3 text-base text-neutral-950 outline-none placeholder:text-neutral-400 dark:text-white dark:placeholder:text-neutral-500 sm:text-lg"
            aria-label={inputAriaLabel}
            autoComplete="off"
            spellCheck="false"
            {...inputA11yProps}
          />

          <button
            type="button"
            onClick={onClose}
            className="ml-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-950 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-white"
            aria-label="Close product search"
          >
            <X size={19} />
          </button>
        </div>

        <div className="max-h-[65vh] min-h-52 overflow-y-auto p-4 sm:p-5">
          <div className="mb-4 flex items-center justify-between gap-4">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-neutral-500 dark:text-neutral-400">
              {isSearchQuery ? "Products" : "Trending products"}
            </p>
            {isSearchQuery && !loading ? (
              <p className="text-xs text-neutral-400 dark:text-neutral-500">
                {items.length} {items.length === 1 ? "match" : "matches"}
              </p>
            ) : null}
          </div>

          {loading ? (
            <ModalProductGridSkeleton label="Loading product suggestions" />
          ) : items.length > 0 ? (
            <div
              id="search-suggestions-listbox"
              role="listbox"
              aria-label={isSearchQuery ? "Matching products" : "Trending products"}
              className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4"
            >
              {items.map((product, index) => {
                const name = safeName(product);
                const selected = highlightedIndex === index;

                return (
                  <button
                    key={product.id || product._id || `${name}-${index}`}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    onMouseEnter={() => onHighlight?.(index)}
                    onFocus={() => onHighlight?.(index)}
                    onClick={() => onSelect?.(product)}
                    className={`group min-w-0 rounded-xl p-2 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-500 ${selected
                      ? "bg-neutral-100 dark:bg-neutral-800"
                      : "hover:bg-neutral-50 dark:hover:bg-neutral-800/60"
                    }`}
                  >
                    <div className="aspect-[4/5] overflow-hidden rounded-lg bg-neutral-100 dark:bg-neutral-800">
                      <ShopImage
                        src={safeImage(product)}
                        alt={name}
                        imgClassName="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
                        fallbackClassName="flex h-full w-full items-center justify-center px-3 text-center text-xs text-neutral-400 dark:text-neutral-500"
                        fallbackLabel="No image"
                        loading="lazy"
                      />
                    </div>
                    <p className="mt-3 line-clamp-2 text-sm font-medium leading-5 text-neutral-900 dark:text-neutral-100">
                      {name}
                    </p>
                    <p className="mt-1 text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                      {formatPrice(product)}
                    </p>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="flex min-h-36 items-center justify-center text-center text-sm text-neutral-500 dark:text-neutral-400">
              {isSearchQuery
                ? `No products match “${normalizedQuery}”.`
                : "No trending products are available yet."}
            </div>
          )}

          {isSearchQuery ? (
            <div className="mt-5 flex justify-center border-t border-neutral-200 pt-4 dark:border-neutral-800">
              <button
                type="button"
                onClick={onViewAll}
                className="rounded-full border border-neutral-900 px-5 py-2 text-xs font-bold uppercase tracking-[0.18em] text-neutral-900 transition hover:bg-neutral-900 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-500 dark:border-neutral-100 dark:text-neutral-100 dark:hover:bg-neutral-100 dark:hover:text-neutral-950"
              >
                View all results
              </button>
            </div>
          ) : null}
        </div>
      </section>
    </div>,
    document.body
  );
}
